import React from 'react'
import { Tooltip as ReactTooltip } from 'react-tooltip'
import styled from 'styled-components'

import { ReactComponent as LeafIcon } from '@/assets/svgs/leaf-eco.svg'
import { IconGenerator } from '@/components/uikit/IconGenerator'
import { ALTOVITA_ECOSTATS_LINK } from '@/constants/links'
import { useNanoId } from '@/hooks/useNanoId'
import { theme } from '@/theme'

import type { PlacesType } from 'react-tooltip'

export type EcoStatsTooltipProps = {
  co2Kgs?: number
  place?: PlacesType
}

const Anchor = styled.div`
  background: ${theme.colors.diminishedLime};
  border-radius: 8px;
  padding: 8px 12px;

  color: ${theme.colors.verdunGreen};
  font-weight: ${theme.fontWeight.medium};

  width: fit-content;
  display: flex;
  column-gap: 6px;
  align-items: center;
`

const Tooltip = styled(ReactTooltip)`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  opacity: 1;

  z-index: 1;

  background-color: ${theme.colors.white};
  box-shadow: -4px 2px 19.1px 0px #00000038;
  border-radius: 8px;
  padding: 14px 21px;

  max-width: 250px;

  color: ${theme.colors.sapphireDark};
  font-size: 14px;
  line-height: 100%;
  text-align: justify;
`

const EcoStatsLink = styled.a`
  color: ${theme.colors.abiBlue};
  text-decoration: none;
`

const TooltipLeafIcon = styled(IconGenerator)`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${theme.colors.dryHighlighterGreen};
  color: ${theme.colors.white};
`

const TooltipCO2AmountWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 15px;
`

const EquivalentCO2eText = styled.div`
  font-size: 16px;
  line-height: 21px;
`

const TransparentText = styled.span`
  opacity: 0.5;
`

const TooltipCO2Amount = styled.div`
  font-weight: ${theme.fontWeight.medium};
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.dryHighlighterGreen};
`

export const EcoStatsTooltip = ({
  co2Kgs = 0,
  place = 'right',
}: EcoStatsTooltipProps) => {
  const id = useNanoId()
  const co2WithUnits = (
    <>
      {co2Kgs.toFixed(2)}kg CO<sub>2</sub>e
    </>
  )

  return (
    <>
      <Anchor data-tooltip-id={id}>
        <IconGenerator iconComponent={LeafIcon} size={20} />
        <span>{co2WithUnits}</span>
      </Anchor>
      <Tooltip id={id} place={place} clickable>
        <EquivalentCO2eText>
          Property equivalent CO2e{' '}
          <TransparentText>/ night stayed</TransparentText>
        </EquivalentCO2eText>
        <div>
          This represents the industry standard. For further details on our
          carbon calculator, please refer to our{' '}
          <EcoStatsLink
            href={ALTOVITA_ECOSTATS_LINK}
            target="_blank"
            rel="noreferrer"
          >
            EcoStats
          </EcoStatsLink>{' '}
          Page.
        </div>
        <TooltipCO2AmountWrapper>
          <TooltipLeafIcon
            iconComponent={LeafIcon}
            viewBox="-8 -6 32 32"
            size={20}
          />
          <TooltipCO2Amount>{co2WithUnits}</TooltipCO2Amount>
        </TooltipCO2AmountWrapper>
      </Tooltip>
    </>
  )
}
